/**
 * Середовище для інтерфейсу (docs/ux/brand/README.md, розд. 6). Звідки береться: атрибут
 * `<html data-env>`, який ставить edge (Caddy `templates`) або Vite-плагін у dev:
 * немає атрибута — prod; `local` — «ЛОКАЛЬНО»; решта значень — «ТЕСТ».
 * Смуга в шапці й на вході читає атрибут у CSS; тут — слово для плашки й заголовка вкладки.
 */
import { useEffect } from 'react';
import { common as tc } from '../i18n/uk/common.ts';
import { env as t } from '../i18n/uk/env.ts';

export type AppEnv = 'stage' | 'local' | null;

export function appEnv(root: HTMLElement = document.documentElement): AppEnv {
  const value = root.getAttribute('data-env');
  if (value === null) return null;
  return value === 'local' ? 'local' : 'stage';
}

/** Слово маркера і прихований опис для читачів екрана; `null` на prod. */
export function envLabel(env: AppEnv): { word: string; hint: string } | null {
  if (env === 'local') return { word: t.envLocal, hint: t.envLocalHint };
  if (env === 'stage') return { word: t.envStage, hint: t.envStageHint };
  return null;
}

/**
 * Позначка непрочитаних сповіщень на початку заголовка (NTF-18): «(3) Мої угоди · Clientline».
 * Ставить і застосовує до поточного заголовка жива частина дзвіночка
 * (`features/notifications/BellLive.tsx`, лінивий чанк); тут — лише стан для `pageTitle`.
 */
let titleBadge = '';

/**
 * Заголовок вкладки (бренд-бук, розд. 3): `<що на сторінці> · Clientline`, поза prod — слово
 * середовища першим: `ТЕСТ · Мої … · Clientline`. Назву запису передавати, лише якщо людина
 * має право її бачити: заголовок потрапляє в історію браузера.
 */
export function pageTitle(page: string | undefined, env: AppEnv = appEnv()): string {
  return titleBadge + [envLabel(env)?.word, page, tc.appName].filter(Boolean).join(' · ');
}

/** Позначка для наступних заголовків: `'(3) '` чи `''`. */
export const setTitleBadge = (badge: string): void => {
  titleBadge = badge;
};

/** Заголовок, збережений раніше (шар повторного входу, поточний), — з позначкою, що діє зараз. */
export const withTitleBadge = (title: string): string =>
  titleBadge + title.replace(/^\(\d+\+?\) /, '');

/** `false` — заголовок вкладки не чіпати (вбудована картка в панелі: заголовок дає сторінка). */
export function usePageTitle(page: string | undefined | false): void {
  useEffect(() => {
    if (page === false) return;
    document.title = pageTitle(page);
  }, [page]);
}
